import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import useBreakpoint from '../hooks/useBreakpoint';
import { useTheme } from '../ThemeContext';

const FREQS = ['Weekly', 'Biweekly', 'Monthly', 'Quarterly', 'Semi-annually', 'Yearly'];
const ANNUAL = { Weekly: 52, Biweekly: 26, Monthly: 12, Quarterly: 4, 'Semi-annually': 2, Yearly: 1 };
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const TODAY = new Date().toISOString().split('T')[0];

const fmt = (n) => '$' + Math.abs(Number(n)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const newId = () => Math.random().toString(36).substr(2, 9);
const ordinal = (n) => { const s = ['th','st','nd','rd']; const v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };

function paymentHitsOnDate(payment, date) {
  const d = date.getDate(), m = date.getMonth(), y = date.getFullYear();
  const lastOcc = payment.lastOccurrence ? new Date(payment.lastOccurrence + 'T00:00:00') : null;
  switch (payment.frequency) {
    case 'Weekly':
      if (lastOcc) { const diff = Math.round((date - lastOcc) / 86400000); return diff >= 0 && diff % 7 === 0; }
      return date.getDay() === 1;
    case 'Biweekly':
      if (lastOcc) { const diff = Math.round((date - lastOcc) / 86400000); return diff >= 0 && diff % 14 === 0; }
      return false;
    case 'Monthly':
      return lastOcc ? d === lastOcc.getDate() : d === 1;
    case 'Quarterly': {
      const ref = lastOcc || new Date(y, 0, 1);
      const md = (y - ref.getFullYear()) * 12 + (m - ref.getMonth());
      return md >= 0 && md % 3 === 0 && d === (lastOcc ? lastOcc.getDate() : 1);
    }
    case 'Semi-annually': {
      const ref = lastOcc || new Date(y, 0, 1);
      const md = (y - ref.getFullYear()) * 12 + (m - ref.getMonth());
      return md >= 0 && md % 6 === 0 && d === (lastOcc ? lastOcc.getDate() : 1);
    }
    case 'Yearly':
      return lastOcc ? m === lastOcc.getMonth() && d === lastOcc.getDate() : m === 0 && d === 1;
    default: return false;
  }
}

function calcPayoff(debts, extraPayment, strategy) {
  let debtsCopy = debts.map(d => ({
    ...d,
    remaining: d.balance,
    totalInterest: 0,
    totalPaid: 0,
  })).filter(d => d.remaining > 0);

  const results = {};
  let month = 0;
  const MAX_MONTHS = 600;

  while (debtsCopy.some(d => d.remaining > 0) && month < MAX_MONTHS) {
    month++;
    const active = debtsCopy.filter(d => d.remaining > 0);
    active.sort((a, b) => strategy === 'avalanche'
      ? b.interestRate - a.interestRate
      : a.remaining - b.remaining
    );

    active.forEach(d => {
      const interest = d.remaining * (d.interestRate / 100) / 12;
      d.remaining += interest;
      d.totalInterest += interest;
    });

    active.forEach(d => {
      const payment = Math.min(d.minPayment || 10, d.remaining);
      d.remaining -= payment;
      d.totalPaid += payment;
    });

    let extra = extraPayment;
    for (const d of active) {
      if (d.remaining <= 0) continue;
      const payment = Math.min(extra, d.remaining);
      d.remaining -= payment;
      d.totalPaid += payment;
      extra -= payment;
      if (extra <= 0) break;
    }

    active.forEach(d => {
      if (d.remaining <= 0.01 && !results[d.id]) {
        results[d.id] = { name: d.name, months: month, totalInterest: d.totalInterest, totalPaid: d.totalPaid };
        d.remaining = 0;
      }
    });

    debtsCopy = debtsCopy.map(d => active.find(a => a.id === d.id) || d);
  }

  return debts.map(d => results[d.id] || { name: d.name, months: null, totalInterest: 0, totalPaid: 0 });
}

export default function DebtTracker({ title, debts, isAuthenticated, isPro, onTitleChange, onDebtsChange, onSignInClick, onUpgradeClick, currency }) {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isDesktop = bp === 'desktop';
  const { t } = useTheme();
  const [tutorialStep, setTutorialStep] = useState(() => {
    try { return localStorage.getItem('bpp_debt_tutorial_seen') ? null : 1; } catch { return 1; }
  });
  const dismissTutorial = () => {
    try { localStorage.setItem('bpp_debt_tutorial_seen', '1'); } catch {}
    setTutorialStep(null);
  };
  const [showTip, setShowTip] = useState(true);
  const [draft, setDraft] = useState({ name: '', balance: '', interestRate: '25.99', compoundDate: '1', minPayment: '' });
  const [payDraft, setPayDraft] = useState({ debtId: '', name: '', amount: '', frequency: 'Monthly', lastOccurrence: '' });
  const [calYear, setCalYear] = useState(() => new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(() => new Date().getMonth());
  const [projMonths, setProjMonths] = useState(24);
  const [payoffStrategy, setPayoffStrategy] = useState('avalanche');
  const [payoffExtra, setPayoffExtra] = useState('0');

  const addDebt = () => {
    if (!draft.name) return;
    onDebtsChange([...debts, {
      id: newId(),
      name: draft.name,
      balance: parseFloat(draft.balance) || 0,
      interestRate: parseFloat(draft.interestRate) || 25.99,
      compoundDate: Math.min(28, Math.max(1, parseInt(draft.compoundDate) || 1)),
      minPayment: parseFloat(draft.minPayment) || 0,
      payments: [],
    }]);
    setDraft({ name: '', balance: '', interestRate: '25.99', compoundDate: '1', minPayment: '' });
  };

  const deleteDebt = (id) => onDebtsChange(debts.filter((d) => d.id !== id));

  const addPayment = () => {
    if (!payDraft.debtId || !payDraft.amount) return;
    onDebtsChange(debts.map(d => d.id === payDraft.debtId
      ? { ...d, payments: [...(d.payments || []), {
          id: newId(),
          name: payDraft.name || `${d.name} Payment`,
          amount: parseFloat(payDraft.amount) || 0,
          frequency: payDraft.frequency,
          lastOccurrence: payDraft.lastOccurrence || null,
        }] }
      : d
    ));
    setPayDraft({ debtId: payDraft.debtId, name: '', amount: '', frequency: 'Monthly', lastOccurrence: '' });
  };

  const deletePayment = (debtId, payId) => {
    onDebtsChange(debts.map(d => d.id === debtId
      ? { ...d, payments: (d.payments || []).filter(p => p.id !== payId) }
      : d
    ));
  };

  // Totals
  const totalBalance = debts.reduce((s, d) => s + (d.balance || 0), 0);
  const totalMinPayment = debts.reduce((s, d) => s + (d.minPayment || 0), 0);
  const totalMonthlyInterest = debts.reduce((s, d) => s + ((d.balance || 0) * (d.interestRate / 100)) / 12, 0);
  const totalMonthlyPayments = debts.reduce((s, d) =>
    s + (d.payments || []).reduce((ps, p) => ps + (p.amount * ANNUAL[p.frequency]) / 12, 0), 0);

  // Compound interest projection (configurable months)
  const projectionData = useMemo(() => {
    if (debts.length === 0) return [];
    const today = new Date();
    let balances = debts.map(d => ({ id: d.id, bal: d.balance || 0 }));
    const data = [];
    for (let i = 0; i <= projMonths; i++) {
      const snap = balances;
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const lbl = MONTH_LABELS[d.getMonth()] + (i === 0 || d.getMonth() === 0 ? ' \'' + String(d.getFullYear()).slice(2) : '');
      const totalBal = snap.reduce((s, b) => s + Math.max(0, b.bal), 0);
      const totalInt = debts.reduce((s, debt) => {
        const b = snap.find(x => x.id === debt.id);
        return s + Math.max(0, b?.bal || 0) * (debt.interestRate / 100 / 12);
      }, 0);
      data.push({ month: lbl, Balance: parseFloat(totalBal.toFixed(2)), Interest: parseFloat(totalInt.toFixed(2)) });
      balances = snap.map(b => {
        const debt = debts.find(dd => dd.id === b.id);
        if (!debt) return b;
        const interest = Math.max(0, b.bal) * (debt.interestRate / 100 / 12);
        const pay = (debt.payments || []).reduce((s, p) => s + (p.amount * ANNUAL[p.frequency]) / 12, 0);
        return { ...b, bal: Math.max(0, b.bal + interest - pay) };
      });
    }
    return data;
  }, [debts, projMonths]);

  // Per-debt amortization: payoff month, total interest
  const debtAmortization = useMemo(() => {
    return debts.map(debt => {
      let bal = debt.balance || 0;
      const monthlyPay = (debt.payments || []).reduce((s, p) => s + (p.amount * ANNUAL[p.frequency]) / 12, 0);
      if (monthlyPay === 0) return { id: debt.id, payoffDate: null, totalInterest: null };
      let totalInterest = 0;
      const today = new Date();
      for (let i = 1; i <= 600; i++) { // up to 50 years
        const interest = bal * (debt.interestRate / 100 / 12);
        totalInterest += interest;
        bal = bal + interest - monthlyPay;
        if (bal <= 0) {
          const pd = new Date(today.getFullYear(), today.getMonth() + i, 1);
          return {
            id: debt.id,
            payoffDate: pd.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
            totalInterest,
          };
        }
      }
      return { id: debt.id, payoffDate: 'Over 50 yrs', totalInterest };
    });
  }, [debts]);

  // Calendar helpers
  const navMonth = (delta) => {
    const d = new Date(calYear, calMonth + delta, 1);
    setCalYear(d.getFullYear()); setCalMonth(d.getMonth());
  };
  const daysInCal = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDow = new Date(calYear, calMonth, 1).getDay();
  const todayRef = new Date(); todayRef.setHours(0,0,0,0);
  const isToday = (day) => calYear === todayRef.getFullYear() && calMonth === todayRef.getMonth() && day === todayRef.getDate();

  const getDayEvents = (day) => {
    const date = new Date(calYear, calMonth, day);
    const events = [];
    debts.forEach(debt => {
      if ((debt.compoundDate || 1) === day)
        events.push({ label: `${debt.name} interest`, color: '#ba1a1a' });
    });
    debts.forEach(debt => {
      (debt.payments || []).forEach(p => {
        if (paymentHitsOnDate(p, date))
          events.push({ label: p.name || `${debt.name} payment`, color: '#1b6d24' });
      });
    });
    return events;
  };

  const allPayments = debts.flatMap(d => (d.payments || []).map(p => ({ ...p, debtName: d.name, debtId: d.id })));
  const hasPayments = allPayments.length > 0;

  // Responsive sizing
  const pagePadding = isDesktop ? '20px' : isTablet ? '16px' : '10px';
  const thPadding = isDesktop ? '10px 16px' : isTablet ? '8px 12px' : '6px 8px';
  const tdPadding = isDesktop ? '10px 16px' : isTablet ? '8px 12px' : '6px 8px';
  const cellFontSize = isDesktop ? '14px' : isTablet ? '13px' : '12px';
  const sectionTitleFontSize = isDesktop ? '15px' : isTablet ? '14px' : '13px';
  const projChartHeight = isDesktop ? 220 : isTablet ? 180 : 160;
  const payFormGap = isMobile ? '8px' : '12px';
  const totalsGrid = isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)';
  const chartMinWidth = isTablet ? '260px' : '300px';

  const dynTh = { padding: thPadding, textAlign: 'left', fontWeight: '700', fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textSecondary };
  const dynTd = { padding: tdPadding, fontSize: cellFontSize };

  if (!isAuthenticated) {
    return (
      <div style={{ minHeight: 'calc(100vh - 94px)', background: t.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ textAlign: 'center', maxWidth: '460px' }}>
          <div style={{ fontSize: '40px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '16px', color: t.text }}>Debt</div>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: '700', color: t.text }}>Debt Tracker</h2>
          <p style={{ margin: '0 0 28px', color: t.textMuted, fontSize: '14px', lineHeight: '1.6' }}>
            Track every credit card and loan with compound interest calculations, up to 60-month payoff
            projections, a payment calendar, and exact payoff dates with total interest paid.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '28px', filter: 'blur(2px)', pointerEvents: 'none', opacity: 0.6 }}>
            {[
              { name: 'Visa Credit Card', balance: '$4,250', rate: '19.99%', color: '#ba1a1a' },
              { name: 'Car Loan', balance: '$12,400', rate: '6.5%', color: '#ff9800' },
              { name: 'Student Loan', balance: '$18,750', rate: '5.25%', color: '#0061a4' },
              { name: 'Line of Credit', balance: '$3,100', rate: '8.75%', color: '#9a25ae' },
            ].map((d) => (
              <div key={d.name} style={{ background: t.card, borderRadius: '10px', padding: '14px', boxShadow: t.shadow, textAlign: 'left' }}>
                <div style={{ height: '4px', background: d.color, borderRadius: '2px', marginBottom: '8px' }} />
                <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '4px', color: t.text }}>{d.name}</div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: d.color, marginBottom: '2px' }}>{d.balance}</div>
                <div style={{ fontSize: '11px', color: t.textMuted }}>Rate: {d.rate}</div>
              </div>
            ))}
          </div>
          <button
            onClick={onSignInClick}
            style={{ padding: '13px 36px', background: '#0061a4', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Sign In to Unlock
          </button>
          <div style={{ marginTop: '10px', fontSize: '12px', color: t.textFaintest }}>Free account required</div>
        </div>
      </div>
    );
  }

  if (tutorialStep !== null) {
    const dotStyle = (step) => ({
      width: '8px', height: '8px', borderRadius: '50',
      display: 'inline-block',
      background: tutorialStep === step ? '#0061a4' : 'transparent',
      border: '1.5px solid #0061a4',
      margin: '0 3px',
    });
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ maxWidth: '480px', width: '100%', background: t.card, borderRadius: '16px', padding: '32px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
          {tutorialStep === 1 && (
            <>
              <h2 style={{ margin: '0 0 14px', fontSize: '20px', fontWeight: '700', color: t.text }}>What Counts as Debt?</h2>
              <p style={{ margin: '0 0 24px', fontSize: '14px', color: t.textSecondary, lineHeight: '1.6' }}>
                Debt is money you've borrowed that you owe back with interest — like credit cards, car loans, student loans, or a line of credit. Bills like Netflix or phone are not debt, even if they feel stressful. Add those in the Budget Planner instead.
              </p>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <span style={dotStyle(1)} /><span style={dotStyle(2)} /><span style={dotStyle(3)} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={dismissTutorial} style={{ background: 'none', border: 'none', color: t.textMuted, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>Skip</button>
                <button onClick={() => setTutorialStep(2)} style={{ background: '#0061a4', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 22px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>Next →</button>
              </div>
            </>
          )}
          {tutorialStep === 2 && (
            <>
              <h2 style={{ margin: '0 0 14px', fontSize: '20px', fontWeight: '700', color: t.text }}>What You'll Need</h2>
              <ul style={{ margin: '0 0 24px', padding: '0 0 0 18px', fontSize: '14px', color: t.textSecondary, lineHeight: '1.8' }}>
                <li><strong>Account name</strong> — what you call it (e.g. Visa, Car Loan)</li>
                <li><strong>Current balance</strong> — what you owe right now</li>
                <li><strong>Interest rate</strong> — look for APR on your statement. Not sure? Try 20% for credit cards, 5% for car or student loans</li>
                <li><strong>Minimum monthly payment</strong> — the smallest amount required each month</li>
              </ul>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <span style={dotStyle(1)} /><span style={dotStyle(2)} /><span style={dotStyle(3)} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={() => setTutorialStep(1)} style={{ background: 'none', border: 'none', color: t.textMuted, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>← Back</button>
                <button onClick={() => setTutorialStep(3)} style={{ background: '#0061a4', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 22px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>Next →</button>
              </div>
            </>
          )}
          {tutorialStep === 3 && (
            <>
              <h2 style={{ margin: '0 0 14px', fontSize: '20px', fontWeight: '700', color: t.text }}>What Happens Next</h2>
              <p style={{ margin: '0 0 24px', fontSize: '14px', color: t.textSecondary, lineHeight: '1.6' }}>
                Once you add a debt, the app calculates how interest compounds month to month. Add installment payments below and they'll automatically appear in your Budget Planner and Calendar — no double entry needed.
              </p>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <span style={dotStyle(1)} /><span style={dotStyle(2)} /><span style={dotStyle(3)} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={() => setTutorialStep(2)} style={{ background: 'none', border: 'none', color: t.textMuted, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>← Back</button>
                <button onClick={dismissTutorial} style={{ background: '#0061a4', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 22px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>Got it — let's start</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...st.page, background: t.pageBg, padding: pagePadding }}>
      <div style={st.container}>

        {/* How-to tip */}
        {showTip && (
          <div style={st.tip}>
            <div style={{ flex: 1 }}>
              <strong>How to use:</strong> Add each credit card or loan below. Enter the current balance, the annual interest rate (most credit cards are <strong>19.99–25.99%</strong>), and the day of the month your statement closes and interest is charged. Then scroll down to add your regular installment payments — the calendar and projection will update automatically.
            </div>
            <button onClick={() => setShowTip(false)} style={st.tipClose}>✕</button>
          </div>
        )}

        {/* Title */}
        <div style={{ ...st.card, background: t.card }}>
          <input type="text" value={title} onChange={(e) => onTitleChange(e.target.value)} placeholder="Title..." style={{ ...st.titleInput, color: t.textMuted }} />
        </div>

        {/* Debt table */}
        <div style={{ ...st.card, background: t.card, marginTop: '16px', overflowX: 'auto' }}>
          <table style={st.table}>
            <thead>
              <tr style={{ background: t.tableHeader }}>
                <th style={dynTh}>Name</th>
                <th style={dynTh}>Current Balance</th>
                <th style={dynTh}>Annual Rate (%)</th>
                <th style={dynTh}>Min. Payment</th>
                <th style={{ ...dynTh, width: '160px' }}>Interest Charges On</th>
                <th style={{ ...dynTh, width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {debts.map((debt) => (
                <tr key={debt.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                  <td style={{ ...dynTd, color: t.textSecondary }}>{debt.name}</td>
                  <td style={{ ...dynTd, color: '#e65100', fontWeight: '700' }}>{fmt(debt.balance)}</td>
                  <td style={{ ...dynTd, color: t.textSecondary }}>{debt.interestRate}%</td>
                  <td style={{ ...dynTd, color: t.textSecondary }}>{fmt(debt.minPayment)}</td>
                  <td style={{ ...dynTd, color: t.textSecondary }}>
                    <span style={{ fontSize: '12px', color: '#ba1a1a', fontWeight: '500' }}>
                      {debt.compoundDate ? `${ordinal(debt.compoundDate)} of month` : '—'}
                    </span>
                  </td>
                  <td style={{ ...dynTd, textAlign: 'center' }}>
                    <button onClick={() => deleteDebt(debt.id)} style={{ ...st.deleteBtn, color: t.textFaintest }}>×</button>
                  </td>
                </tr>
              ))}
              {/* Input row */}
              <tr style={{ background: t.tableInputRow }}>
                <td style={dynTd}><input type="text" value={draft.name} onChange={(e) => setDraft(p => ({ ...p, name: e.target.value }))} placeholder="What do you call this debt?" style={{ ...st.cellInput, color: t.text }} onKeyDown={(e) => e.key === 'Enter' && addDebt()} /></td>
                <td style={dynTd}><input type="number" value={draft.balance} onChange={(e) => setDraft(p => ({ ...p, balance: e.target.value }))} placeholder="How much do you owe?" style={{ ...st.cellInput, color: t.text }} min="0" step="0.01" /></td>
                <td style={dynTd}><input type="number" value={draft.interestRate} onChange={(e) => setDraft(p => ({ ...p, interestRate: e.target.value }))} placeholder="25.99" title="Look for APR on your statement. Not sure? Try 20% for credit cards, 5% for car or student loans" style={{ ...st.cellInput, color: t.text }} min="0" step="0.01" /></td>
                <td style={dynTd}><input type="number" value={draft.minPayment} onChange={(e) => setDraft(p => ({ ...p, minPayment: e.target.value }))} placeholder="Minimum monthly payment" style={{ ...st.cellInput, color: t.text }} min="0" step="0.01" /></td>
                <td style={dynTd}><input type="number" value={draft.compoundDate} onChange={(e) => setDraft(p => ({ ...p, compoundDate: e.target.value }))} placeholder="Due date (day 1–28)" style={{ ...st.cellInput, color: t.text }} min="1" max="28" /></td>
                <td style={dynTd}></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ fontSize: '12px', color: t.textMuted, padding: '8px 4px', lineHeight: '1.5' }}>
          ℹ Your debt payments are automatically included in your budget and calendar. You don't need to add them separately.
        </div>

        {/* Totals row */}
        <div style={{ ...st.card, background: t.card, marginTop: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: totalsGrid }}>
            {[
              { label: 'Total Balance', value: fmt(totalBalance), color: '#e65100', tooltip: 'The combined outstanding balance across all your debts.' },
              { label: 'Total Min. Payment', value: fmt(totalMinPayment), color: '#0061a4', tooltip: 'The minimum monthly payment required across all your debts.' },
              { label: 'Est. Monthly Interest', value: fmt(totalMonthlyInterest), color: '#9a25ae', tooltip: 'Estimated interest your debts will generate this month based on current balances and rates.' },
              { label: 'Monthly Payments', value: fmt(totalMonthlyPayments), color: '#1b6d24', tooltip: 'Total amount going toward debt payments from your chequing account this month.' },
            ].map((item, i) => (
              <div key={i} style={{
                borderLeft: (isMobile ? (i % 2 !== 0) : i > 0) ? `1px solid ${t.border}` : 'none',
                borderTop: (isMobile && i >= 2) ? `1px solid ${t.border}` : 'none',
              }}>
                <div style={{ padding: '12px 16px', textAlign: 'center', fontSize: '15px', fontWeight: '700', color: item.color }}>{item.value}</div>
                <div style={{ padding: '8px 16px', textAlign: 'center', fontSize: '10px', fontWeight: '700', color: t.textMuted, background: t.border, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.label}
                  <button title={item.tooltip} style={{ fontSize: '11px', color: t.textFaintest, cursor: 'help', marginLeft: '4px', background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', lineHeight: 1 }}>ℹ</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ textAlign: 'center', margin: '24px 0 20px' }}>
          <button onClick={addDebt} style={st.addBtn}>Add Debt +</button>
        </div>

        {debts.length > 0 && (<>

          {/* Compound interest projection + amortization table */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap' }}>

            <div style={{ ...st.card, background: t.card, flex: 2, minWidth: chartMinWidth, padding: '18px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                <h3 style={{ ...st.sectionTitle, margin: 0, color: t.textSub, fontSize: sectionTitleFontSize }}>Compound Interest Projection</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', color: t.textMuted }}>Show:</span>
                  {[12, 24, 36, 48, 60].map(m => {
                    const locked = !isAuthenticated && m > 24;
                    return (
                      <button key={m} onClick={() => !locked && setProjMonths(m)} style={{
                        padding: isMobile ? '3px 6px' : '3px 8px', fontSize: '11px', fontWeight: projMonths === m ? '700' : '400',
                        border: projMonths === m ? '2px solid #ba1a1a' : `1px solid ${t.borderFaint}`,
                        borderRadius: '4px', background: locked ? t.pageBg : projMonths === m ? '#ffdad6' : t.card,
                        color: locked ? t.textFaintest : projMonths === m ? '#ba1a1a' : t.textSecondary,
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}>
                        {m}mo{locked ? ' (Pro)' : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
              <p style={{ fontSize: '12px', color: t.textMuted, margin: '0 0 14px' }}>
                Total debt balance over time based on your interest rates and installment payments.
                {totalMonthlyPayments === 0 && <span style={{ color: '#ba1a1a' }}> Add payments below to see your payoff trajectory.</span>}
              </p>
              <ResponsiveContainer width="100%" height={projChartHeight}>
                <LineChart data={projectionData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={2} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? '$' + (v/1000).toFixed(1) + 'k' : '$' + v} width={58} />
                  <Tooltip formatter={(v, name) => [fmt(v), name === 'Balance' ? 'Total Debt' : 'Monthly Interest']} />
                  <Line type="monotone" dataKey="Balance" stroke="#ba1a1a" strokeWidth={2.5} dot={{ r: 2.5, fill: '#ba1a1a' }} name="Balance" />
                  <Line type="monotone" dataKey="Interest" stroke="#ff9800" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Interest" />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: t.textMuted, marginTop: '8px' }}>
                <span><span style={{ color: '#ba1a1a', fontWeight: '700' }}>—</span> Total Debt Balance</span>
                <span><span style={{ color: '#ff9800', fontWeight: '700' }}>- -</span> Monthly Interest Added</span>
              </div>
            </div>

            <div style={{ ...st.card, background: t.card, flex: 1, minWidth: isTablet ? '260px' : '260px', overflow: 'hidden' }}>
              <div style={{ background: t.tableSubHeader, padding: '10px 14px', fontSize: '10px', fontWeight: '700', color: t.textMuted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                MONTH-BY-MONTH BREAKDOWN
              </div>
              <div style={{ overflowY: 'auto', maxHeight: '312px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: t.tableHeader }}>
                      {['Month', 'Interest', 'Payments', 'Balance'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textSecondary, position: 'sticky', top: 0, background: t.tableHeader }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {projectionData.slice(0, 13).map((row, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${t.borderLight}`, background: i % 2 ? t.tableRowAlt : t.card }}>
                        <td style={{ padding: '8px 10px', fontSize: '12px', color: t.textSub }}>{row.month}</td>
                        <td style={{ padding: '8px 10px', fontSize: '12px', color: '#ba1a1a' }}>{fmt(row.Interest)}</td>
                        <td style={{ padding: '8px 10px', fontSize: '12px', color: '#1b6d24' }}>{fmt(totalMonthlyPayments)}</td>
                        <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: '600', color: t.text }}>{fmt(row.Balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Per-debt payoff summary */}
          {debtAmortization.some(a => a.payoffDate) && (
            <div style={{ ...st.card, background: t.card, marginBottom: '16px', overflow: 'hidden' }}>
              <div style={{ background: t.tableSubHeader, padding: '10px 16px', fontSize: '10px', fontWeight: '700', color: t.textMuted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                DEBT PAYOFF SUMMARY
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: t.tableHeader }}>
                    {['Debt', 'Balance', 'Monthly Payment', 'Est. Payoff Date', 'Total Interest'].map(h => (
                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textSecondary }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {debts.map(debt => {
                    const am = debtAmortization.find(a => a.id === debt.id);
                    const monthlyPay = (debt.payments || []).reduce((s, p) => s + (p.amount * ANNUAL[p.frequency]) / 12, 0);
                    return (
                      <tr key={debt.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                        <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: '500', color: t.text }}>{debt.name}</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', color: '#e65100', fontWeight: '700' }}>{fmt(debt.balance)}</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', color: '#1b6d24' }}>{monthlyPay > 0 ? fmt(monthlyPay) : <span style={{ color: t.textFaintest }}>No payments</span>}</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: '600', color: am?.payoffDate ? '#0061a4' : '#bfc7d4' }}>
                          {am?.payoffDate || '—'}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', color: '#ba1a1a' }}>
                          {am?.totalInterest != null ? fmt(am.totalInterest) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Installment Payments */}
          <div style={{ ...st.card, background: t.card, marginBottom: '16px', overflowX: 'auto' }}>
            <div style={{ background: t.tableSubHeader, padding: '10px 16px', fontSize: '10px', fontWeight: '700', color: t.textMuted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              INSTALLMENT PAYMENTS FROM CHEQUING
            </div>

            {hasPayments && (
              <table style={{ ...st.table }}>
                <thead>
                  <tr style={{ background: t.tableHeader }}>
                    <th style={dynTh}>Applies To</th>
                    <th style={dynTh}>Payment Name</th>
                    <th style={dynTh}>Amount</th>
                    <th style={dynTh}>Frequency</th>
                    <th style={{ ...dynTh, width: '140px' }}>Last Payment</th>
                    <th style={{ ...dynTh, width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {allPayments.map((p) => (
                    <tr key={p.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                      <td style={{ ...dynTd, color: '#9a25ae', fontWeight: '500' }}>{p.debtName}</td>
                      <td style={{ ...dynTd, color: t.textSecondary }}>{p.name}</td>
                      <td style={{ ...dynTd, color: '#1b6d24', fontWeight: '600' }}>{fmt(p.amount)}</td>
                      <td style={{ ...dynTd, color: t.textSecondary }}>{p.frequency}</td>
                      <td style={{ ...dynTd, fontSize: '12px', color: t.textMuted }}>
                        {p.lastOccurrence
                          ? new Date(p.lastOccurrence + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </td>
                      <td style={{ ...dynTd, textAlign: 'center' }}>
                        <button onClick={() => deletePayment(p.debtId, p.id)} style={{ ...st.deleteBtn, color: t.textFaintest }}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Add payment row */}
            <div style={{ padding: '14px 16px', background: t.tableInputRow, display: 'flex', gap: payFormGap, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Field label="Applies To">
                <select value={payDraft.debtId} onChange={(e) => setPayDraft(p => ({ ...p, debtId: e.target.value }))} style={{ ...st.select, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}>
                  <option value="">Select debt...</option>
                  {debts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Field>
              <Field label="Payment Name">
                <input type="text" value={payDraft.name} onChange={(e) => setPayDraft(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Visa Payment" style={{ ...st.input, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }} />
              </Field>
              <Field label="Amount">
                <input type="number" value={payDraft.amount} onChange={(e) => setPayDraft(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" style={{ ...st.input, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text, width: '90px' }} min="0" step="0.01" />
              </Field>
              <Field label="Frequency">
                <select value={payDraft.frequency} onChange={(e) => setPayDraft(p => ({ ...p, frequency: e.target.value }))} style={{ ...st.select, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }}>
                  {FREQS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="Last Payment Date">
                <input type="date" value={payDraft.lastOccurrence} onChange={(e) => setPayDraft(p => ({ ...p, lastOccurrence: e.target.value }))} max={TODAY} style={{ ...st.input, border: `1px solid ${t.borderFaint}`, background: t.inputBg, color: t.text }} />
              </Field>
              <button onClick={addPayment} style={{ ...st.addBtn, padding: '8px 22px', fontSize: '14px', borderRadius: '8px', alignSelf: 'flex-end' }}>
                Add +
              </button>
            </div>
          </div>

          {/* Debt Payoff Calculator */}
          <div style={{ ...st.card, background: t.card, marginBottom: '16px' }}>
            <div style={{ background: t.tableSubHeader, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '10px', fontWeight: '700', color: t.textMuted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>DEBT PAYOFF CALCULATOR</span>
              <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', borderRadius: '10px', letterSpacing: '0.04em' }}>PRO</span>
            </div>
            {!isPro ? (
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <p style={{ color: t.textMuted, fontSize: '14px', lineHeight: '1.6', margin: '0 0 16px' }}>
                  Compare avalanche vs. snowball payoff strategies — see exact payoff dates, total interest, and how much extra payments save you.
                </p>
                <button onClick={onUpgradeClick} style={{ background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}>
                  Upgrade to Pro
                </button>
              </div>
            ) : (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '20px' }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Strategy</div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {['avalanche', 'snowball'].map(s => (
                        <button key={s} onClick={() => setPayoffStrategy(s)} style={{
                          border: 'none', borderRadius: '6px', padding: '7px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                          background: payoffStrategy === s ? '#0061a4' : t.borderLight,
                          color: payoffStrategy === s ? 'white' : t.textMuted,
                        }}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Extra Monthly Payment</div>
                    <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${t.borderFaint}`, borderRadius: '7px', overflow: 'hidden', background: t.card }}>
                      <span style={{ padding: '0 8px', color: t.textMuted, borderRight: `1px solid ${t.borderFaint}`, fontSize: '14px', lineHeight: '36px', userSelect: 'none' }}>{fmt(0).charAt(0)}</span>
                      <input type="number" value={payoffExtra} onChange={e => setPayoffExtra(e.target.value)}
                        placeholder="0.00" min="0" step="0.01"
                        style={{ border: 'none', outline: 'none', padding: '8px 10px', fontSize: '14px', fontFamily: 'inherit', color: t.text, background: 'transparent', width: '100px' }} />
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: t.textMuted, lineHeight: '1.5', maxWidth: '260px' }}>
                    <strong>Avalanche:</strong> pay highest interest first — minimizes total interest.<br/>
                    <strong>Snowball:</strong> pay lowest balance first — builds momentum.
                  </div>
                </div>
                {(() => {
                  const extra = parseFloat(payoffExtra) || 0;
                  const results = calcPayoff(debts, extra, payoffStrategy);
                  const maxMonths = Math.max(...results.map(r => r.months || 0));
                  return (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: t.tableHeader }}>
                            {['Debt', 'Payoff Date', 'Months', 'Total Interest', 'Total Paid'].map(h => (
                              <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textSecondary }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {results.map((r, i) => {
                            const payoffDate = r.months
                              ? (() => { const d = new Date(); d.setMonth(d.getMonth() + r.months); return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); })()
                              : null;
                            return (
                              <tr key={i} style={{ borderBottom: `1px solid ${t.border}` }}>
                                <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '500', color: t.text }}>{r.name}</td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '600', color: payoffDate ? '#0061a4' : t.textFaintest }}>{payoffDate || '—'}</td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', color: t.textSecondary }}>{r.months ? r.months + ' mo' : '—'}</td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', color: '#ba1a1a' }}>{r.totalInterest > 0 ? fmt(r.totalInterest) : '—'}</td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', color: '#1b6d24', fontWeight: '600' }}>{r.totalPaid > 0 ? fmt(r.totalPaid) : '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {maxMonths > 0 && (
                          <tfoot>
                            <tr style={{ background: t.border }}>
                              <td colSpan={5} style={{ padding: '10px 12px', fontSize: '12px', color: t.textMuted }}>
                                Debt-free in <strong>{maxMonths} months</strong> ({(() => { const d = new Date(); d.setMonth(d.getMonth() + maxMonths); return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); })()}) using the <strong>{payoffStrategy}</strong> strategy{extra > 0 ? ` with ${fmt(extra)}/mo extra` : ''}.
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Debt Calendar */}
          <div style={{ ...st.card, background: t.card }}>
            <div style={{ background: t.tableSubHeader, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '10px', fontWeight: '700', color: t.textMuted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>DEBT CALENDAR</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={() => navMonth(-1)} style={{ ...st.calNavBtn, background: t.borderLight, color: t.textSub }}>‹</button>
                <span style={{ fontSize: '13px', fontWeight: '600', minWidth: '110px', textAlign: 'center', color: t.text }}>{MONTH_LABELS[calMonth]} {calYear}</span>
                <button onClick={() => navMonth(1)} style={{ ...st.calNavBtn, background: t.borderLight, color: t.textSub }}>›</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', padding: '10px 16px', fontSize: '11px', color: t.textTertiary, background: t.tableRowAlt, flexWrap: 'wrap' }}>
              <span><span style={{ color: '#ba1a1a', fontWeight: '700' }}>●</span> Interest Charged</span>
              <span><span style={{ color: '#1b6d24', fontWeight: '700' }}>●</span> Payment Due</span>
              <span style={{ padding: '1px 7px', background: '#d1e4ff', borderRadius: '4px', color: '#0061a4', fontWeight: '600' }}>Today</span>
            </div>
            <div style={{ padding: '12px 16px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: '600', color: t.textMuted, padding: '4px' }}>{d}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
                {Array.from({ length: firstDow }, (_, i) => <div key={'e' + i} />)}
                {Array.from({ length: daysInCal }, (_, i) => {
                  const day = i + 1;
                  const events = getDayEvents(day);
                  const todayFlag = isToday(day);
                  return (
                    <div key={day} style={{
                      minHeight: '58px',
                      border: todayFlag ? '2px solid #0061a4' : `1px solid ${t.borderLight}`,
                      borderRadius: '6px',
                      background: todayFlag ? 'rgba(0,97,164,0.05)' : t.card,
                      padding: '4px 5px',
                    }}>
                      <div style={{ fontSize: '11px', fontWeight: todayFlag ? '700' : '400', color: todayFlag ? '#0061a4' : t.textSub, marginBottom: '2px' }}>{day}</div>
                      {events.map((ev, ei) => (
                        <div key={ei} style={{ fontSize: '9px', lineHeight: '1.4', color: ev.color, fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          ● {ev.label}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

        </>)}

        <div style={{ textAlign: 'center', padding: '12px', marginTop: '8px' }}>
          <button
            onClick={() => setTutorialStep(1)}
            style={{ background: 'none', border: 'none', color: t.textFaintest, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}
          >
            Show tutorial again
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  const { t } = useTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '11px', fontWeight: '600', color: t.textTertiary, letterSpacing: '0.03em' }}>{label}</label>
      {children}
    </div>
  );
}

const st = {
  page: { minHeight: '100vh', padding: '20px' },
  container: { maxWidth: '1100px', margin: '0 auto' },
  btn: {
    background: '#9a25ae', color: 'white', border: 'none', borderRadius: '8px',
    padding: '10px 18px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', fontFamily: 'inherit',
  },
  tip: {
    background: 'rgba(27,109,36,0.06)', border: '1px solid rgba(27,109,36,0.2)', borderRadius: '8px',
    padding: '14px 16px', marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'flex-start',
    fontSize: '13px', color: '#1b6d24', lineHeight: '1.5',
  },
  tipClose: {
    background: 'none', border: 'none', cursor: 'pointer', color: '#1b6d24',
    fontSize: '16px', padding: '0 2px', fontFamily: 'inherit', lineHeight: 1, flexShrink: 0,
  },
  card: {
    background: 'white', borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden',
  },
  titleInput: {
    width: '100%', border: 'none', outline: 'none', fontSize: '20px',
    padding: '16px 20px', color: '#888', background: 'transparent', fontFamily: 'inherit',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 16px', textAlign: 'left', fontWeight: '700', fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase' },
  td: { padding: '10px 16px', fontSize: '14px' },
  cellInput: {
    width: '100%', border: 'none', outline: 'none',
    background: 'transparent', fontSize: '14px', padding: '2px', fontFamily: 'inherit',
  },
  deleteBtn: {
    background: 'transparent', border: 'none', color: '#ba1a1a',
    cursor: 'pointer', fontSize: '20px', padding: '0 6px', fontFamily: 'inherit', lineHeight: 1,
  },
  addBtn: {
    background: '#8dc88e', color: 'white', border: 'none', borderRadius: '24px',
    padding: '14px 64px', cursor: 'pointer', fontSize: '16px', fontWeight: '500', fontFamily: 'inherit',
  },
  sectionTitle: { margin: '0 0 6px', fontSize: '15px', fontWeight: '700' },
  calNavBtn: {
    background: '#0061a4', border: 'none', borderRadius: '4px', padding: '4px 10px',
    cursor: 'pointer', fontSize: '16px', fontFamily: 'inherit', color: 'white',
  },
  select: {
    border: '1px solid rgba(191,199,212,0.4)', borderRadius: '8px', padding: '7px 10px',
    fontSize: '13px', fontFamily: 'inherit', background: 'white', cursor: 'pointer',
  },
  input: {
    border: '1px solid rgba(191,199,212,0.4)', borderRadius: '8px', padding: '7px 10px',
    fontSize: '13px', fontFamily: 'inherit', background: 'white', width: '150px',
  },
  label: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase' },
};
